'use client';

import { useLayoutEffect, useRef } from 'react';
import debounce from 'lodash.debounce';

import { transformValue, linearInterpolation } from './helpers/transforms';
import { reelVisibleAdjacentCardOffset } from '../../config/config';

interface InitialIndicatorWidths {
    [index: number]: string;
}

const indicatorActiveSize = 64;
const indicatorSize = 4;

const useOnReelScroll = (
    reelRef: React.MutableRefObject<HTMLUListElement | null>,
    indicatorsRef: React.MutableRefObject<HTMLDivElement | null>,
    gapSize: number,
    disabled: boolean,
) => {
    const hasInit = useRef(false);
    const isScrolling = useRef(false);

    // This stores the widths of the indicator against it's index. This is done because if the scroll
    // component re-renders, the width style set below is lost. This is a way to store widths in case of a
    // re-render so the UI widths of the indicators remains consistent. Can't use useState as that would cause
    // an extra re-render when being set and would be too intensive for react given the number of times the
    // scroll function runs.
    const initialIndicatorWidths = useRef<InitialIndicatorWidths>({
        0: `${indicatorActiveSize}px`
    });

    useLayoutEffect(() => {
        const reel = reelRef.current;
        const indicators = indicatorsRef.current;

        // If there are no refs given or CSS Animation timeline is supported.
        // @see https://developer.mozilla.org/en-US/docs/Web/CSS/animation-timeline
        if (!reel || !indicators || disabled) {
            return () => {};
        }

        const updateIndicatorWidths = debounce(() => {
            initialIndicatorWidths.current = {};

            const indicatorList = Array.from(indicators.children);

            // If the scroll position is at the end of the reel.
            if (Math.ceil((reel.scrollLeft + reel.clientWidth)) >= reel.scrollWidth) {
                for (let index = 0; index < indicatorList.length; index += 1) {
                    const width = (index + 1) === indicatorList.length ? indicatorActiveSize : indicatorSize;

                    initialIndicatorWidths.current[index] = `${width}px`;
                    (indicatorList[index] as HTMLElement).style.setProperty('width', `${width}px`);
                }
                isScrolling.current = false;
                return;
            }

            // If the scroll position is at the start of the reel, set the first one to active and the rest
            // inactive.
            if (!reel.scrollLeft) {
                for (let index = 0; index < indicatorList.length; index += 1) {
                    const width = index === 0 ? indicatorActiveSize : indicatorSize;

                    initialIndicatorWidths.current[index] = `${width}px`;
                    (indicatorList[index] as HTMLElement).style.setProperty('width', `${width}px`);
                }
                isScrolling.current = false;
                return;
            }

            // If the scroll position is at the end of the reel, set the last one as active and the rest
            // inactive.
            if (Math.ceil((reel.scrollLeft + reel.clientWidth)) >= reel.scrollWidth) {
                for (let index = 0; index < indicatorList.length; index += 1) {
                    const width = (index + 1) === indicatorList.length ? indicatorActiveSize : indicatorSize;

                    initialIndicatorWidths.current[index] = `${width}px`;
                    (indicatorList[index] as HTMLElement).style.setProperty('width', `${width}px`);
                }
                isScrolling.current = false;
                return;
            }

            // Loop through all the indicators and set their active widths.
            for (let index = 0; index < indicatorList.length; index += 1) {
                const indicator = indicatorList[index] as HTMLElement;
                const reelItem = reel.children[index] as HTMLElement;

                let reelWidth = reelItem.clientWidth;

                const isLastItem = index === (indicatorList.length - 1);
                if (isLastItem) {
                    const paddingRight = parseInt(getComputedStyle(reelItem).getPropertyValue('padding-right'), 10) || 0;
                    reelWidth = isLastItem ? (reelItem.clientWidth - paddingRight) : reelItem.clientWidth
                }

                // Offset is how much of the previous card they can see to indicate the reel is scrollable.
                const customOffset = reelVisibleAdjacentCardOffset + gapSize;
                const offsetLeft = reelItem.offsetLeft - customOffset;

                const percentage = transformValue(
                    (offsetLeft - reelWidth),
                    offsetLeft,
                    (offsetLeft + reelWidth),
                    0,
                    100,
                    0,
                    reel.scrollLeft,
                );

                const width = Math.floor(linearInterpolation(0, 100, indicatorSize, indicatorActiveSize, percentage));

                initialIndicatorWidths.current[index] = `${width}px`;
                indicator.style.setProperty('width', `${width}px`);
            }

            // Allow next animation frame update.
            isScrolling.current = false;
        }, 25);

        const handleScroll = () => {
            // isScrolling stops this function running multiple times.
            if (!isScrolling.current) {
                isScrolling.current = true;
                requestAnimationFrame(updateIndicatorWidths);
            }
        };

        // Set the initial scroll indicator widths.
        if (!hasInit.current) {
            updateIndicatorWidths();
            hasInit.current = true;
        }

        reel.addEventListener('scroll', handleScroll);

        return () => {
            reel.removeEventListener('scroll', handleScroll);
        };
    }, [reelRef, indicatorsRef, disabled, gapSize]);

    return initialIndicatorWidths.current;
};

export default useOnReelScroll;
